import Vue from "vue";
import router from "./src/router";
import VueRouter from "vue-router";
import Vuex from "vuex";
import VuexPersist from "vuex-persist";
import axios from "axios";
import VueMobileDetection from "vue-mobile-detection";
import UUID from "vue-uuid";
import VueTippy from "vue-tippy";
import { trackAction } from "./action-tracking";
import { currency } from "./helpers";
import { get, isInteger } from "lodash-es";
import * as Sentry from "@sentry/vue";
import VModal from "vue-js-modal";

Vue.use(VModal, { dialog: true });

const brand = import.meta.env.VITE_BRAND_ID ?? 1;
window.brand = brand;
window.stripeKey = import.meta.env.VITE_STRIPE_KEY ?? "";
const customerSupportAddress =
    import.meta.env.VITE_BRAND_CUSTOMER_SUPPORT_EMAIL ?? "";
window.cimetEnabled = false;
const raffleEnabled =
    (import.meta.env.VITE_ENABLE_RAFFLES ?? "false") === "true";
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
axios.defaults.headers.common["X-CSRF-TOKEN"] = document
    .querySelector('meta[name="csrf-token"]')
    .getAttribute("content");

Vue.use(VueTippy, {
    theme: "tippla",
});

Vue.use(UUID);
Vue.use(VueMobileDetection);
Vue.use(VueRouter);
Vue.use(Vuex);
// Vue.use(VModal);
// Vue.component(VueCarousel.name, VueCarousel);

Sentry.init({
    Vue,
    dsn: "https://9ac3d4209a664777a68a88df7bf11fd6@o4505232390619136.ingest.sentry.io/4505232400580608",
    ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "Non-Error exception captured",
        "Non-Error promise rejection captured",
        "Avoided redundant navigation",
        "Maximum number of retries reached while waiting for window.ga to appear",
    ],
    integrations: [
        new Sentry.BrowserTracing({
            routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        }),
        new Sentry.Replay(),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0,

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
});

const vuexLocalStorage = new VuexPersist({
    key: "vuex",
    storage: window.localStorage,
});

const store = new Vuex.Store({
    state: {
        LoggedIn: false,
        CreditScores: [],
        VerificationStatus: null,
        CreditReportHistory: [],
        CreditReportHistoryDiffs: [],
        CurrentCreditReportProvider: null,
        MemberName: [],
        AgreementSigned: true,
        BankStatementStatus: null,
        BankStatementMetrics: null,
        NavCollapse: false,
        Member: null,
        MobileNavOpen: false,
        GlobalLoading: false,
        CimetEnabled: cimetEnabled,
        MemberDataLastGrabbed: new Date().getTime(),
        HideSubscription: false,
    },

    getters: {
        SubscriptionPrice(state) {
            if (state.Member && state.Member.discounted_subscription_price) {
                return state.Member.discounted_subscription_price.toFixed(2);
            }
            return "8.99";
        },
        Subscribed(state, getters) {
            return get(state.Member, "subscribed", false);
        },
        IsVerificationFailed(state, getters) {
            return (
                state.Member &&
                state.Member.status !== "Active" &&
                state.Member.status !== "" &&
                state.Member.status !== null
            );
        },
        IsVerified(state) {
            return state.Member && state.Member.status == "Active";
        },
        NeedsVerification(state) {
            return (
                window.brand === 2 ||
                (window.brand === 1 &&
                    state.Member &&
                    (state.Member.status == "" || state.Member.status == null))
            );
        },
        VerificationError(state) {
            return (
                state.Member &&
                state.Member.status != "Active" &&
                state.Member.status != "" &&
                state.Member.status != null
            );
        },
        CurrentCreditReportDiff(state, getters) {
            const providerName = getters.CurrentCreditReportProviderName;
            return state.CreditReportHistoryDiffs[providerName];
        },
        CurrentCreditReport(state) {
            if (
                state.CreditScores &&
                state.CurrentCreditReportProvider &&
                state.CreditScores[state.CurrentCreditReportProvider]
            ) {
                return state.CreditScores[state.CurrentCreditReportProvider];
            }
        },
        CurrentCreditReportProviderName(state) {
            if (state.CurrentCreditReportProvider) {
                switch (state.CurrentCreditReportProvider) {
                    case 1:
                        return "Experian";
                    case 2:
                        return "Equifax";
                    case 3:
                        return "Equifax";
                }
            }
            return null;
        },
        GetBankStatementStatusFromStore(state) {
            // Horrible hack to get around the fact that the store is not reactive in a clearInterval function
            return state.BankStatementStatus;
        },
        HasExperianReport(state, getters) {
            return (
                getters.IsVerified &&
                state?.CreditScores[1] !== null &&
                state?.CreditScores[1]?.CreditScore !== null
            );
        },
        HasEquifaxReport(state, getters) {
            return (
                getters.IsVerified &&
                state?.CreditScores[2] !== null &&
                state?.CreditScores[2]?.CreditScore !== null
            );
        },
    },

    mutations: {
        HideSubscription(state, data) {
            state.HideSubscription = data;
        },
        AgreementSigned(state, data) {
            state.AgreementSigned = data;
        },
        MemberDataLastGrabbed(state, data) {
            state.MemberDataLastGrabbed = data;
        },
        GlobalLoading(state, data) {
            // Check data is a boolean first
            if (typeof data !== "boolean") {
                return;
            }
            state.GlobalLoading = data;
        },
        Member(state, data) {
            state.Member = data;
        },
        CreditScores(state, data) {
            state.CreditScores = data;
        },
        CurrentCreditReportProvider(state, data) {
            state.CurrentCreditReportProvider = data;
        },
        CreditReportHistory(state, data) {
            state.CreditReportHistory = data;
        },
        CreditReportHistoryDiffs(state, data) {
            state.CreditReportHistoryDiffs = data;
        },
        VerificationStatus(state, data) {
            state.VerificationStatus = data;
        },
        BankStatementStatus(state, data) {
            state.BankStatementStatus = data;
        },
        BankStatementMetrics(state, data) {
            state.BankStatementMetrics = data;
        },
        NavCollapse(state, data) {
            state.NavCollapse = data;
        },
        LoggedIn(state, data) {
            state.LoggedIn = data;
        },
        MobileNavOpen(state, data) {
            state.MobileNavOpen = data;
        },
    },

    actions: {
        UpdatePassword(context, data) {
            return new Promise((resolve, reject) => {
                axios
                    .patch("/api/password", data, {
                        withCredentials: true,
                    })
                    .then((res) => {
                        if (res && res.data) {
                            resolve(res.data);
                        } else {
                            reject("UpdatePassword - Content error");
                        }
                    })
                    .catch((err) => {
                        if (err.response.status === 422) {
                            reject({
                                reason: "VALIDATION_FAIL",
                                data: err.response.data.errors,
                            });
                        }
                        reject("UpdatePassword - Response error");
                    });
            });
        },
        ClearState(context, data) {
            this.commit("AgreementSigned", null);
            this.commit("BankStatementMetrics", null);
            this.commit("BankStatementStatus", null);
            this.commit("CreditReportHistory", []);
            this.commit("CreditReportHistoryDiffs", []);
            this.commit("CreditScores", []);
            this.commit("CurrentCreditReportProvider", null);
            this.commit("LoggedIn", false);
            this.commit("Member", null);
            this.commit("VerificationStatus", null);
            this.commit("GlobalLoading", false);
        },
        ToggleMobileMenu(context, data) {
            this.commit("MobileNavOpen", !this.state.MobileNavOpen);
        },
        Logout(context, data) {
            this.commit("GlobalLoading", true);
            axios
                .post("/logout", {
                    withCredentials: true,
                })
                .then((res) => {})
                .finally(() => {
                    this.dispatch("ClearState");
                    location.href = "/login";
                });
        },
        GetLoginState(content, data) {
            return new Promise((resolve, reject) => {
                if (
                    document.getElementById("is_authed") &&
                    document
                        .getElementById("is_authed")
                        .getAttribute("content") == 1
                ) {
                    this.commit("LoggedIn", true);
                } else {
                    this.commit("LoggedIn", false);
                }
                resolve();
            });
        },
        GetDashboardData({ commit }) {
            return new Promise((resolve, reject) => {
                axios
                    .get("/api/member-data", {
                        withCredentials: true,
                    }) // Logged in User
                    .then((res) => {
                        if (res && res.data && res.data.data) {
                            commit("Member", res.data.data);
                            commit(
                                "MemberDataLastGrabbed",
                                new Date().getTime()
                            );
                            resolve();
                        } else {
                            reject("GetDashboardData - Response error");
                        }
                    })
                    .catch((err) => {
                        reject("GetDashboardData - Response error");
                    });
            });
        },

        GetBankStatementStatus(context, data) {
            return new Promise((resolve, reject) => {
                axios
                    .get("/api/bank-statement-status", {
                        withCredentials: true,
                    }) // Logged in User
                    .then((res) => {
                        if (res && res.data) {
                            this.commit("BankStatementStatus", res.data.Status);
                            this.commit(
                                "BankStatementMetrics",
                                res.data.Metrics
                            );
                            resolve();
                        }
                        reject("BankStatementStatus - Response error");
                    })
                    .catch((err) => {
                        reject("BankStatementStatus - Response error");
                    });
            });
        },

        GetCreditScores(context, data) {
            return new Promise((resolve, reject) => {
                axios
                    .get("/api/tipplacreditreport", { withCredentials: true }) // Logged in User
                    .then((res) => {
                        if (res && res.data && res.data.CurrentReports) {
                            this.commit(
                                "CreditScores",
                                res.data.CurrentReports
                            );

                            // Set the current credit report provider
                            // added a condition to set the CurrentCreditReportProvider only if it's null
                            if (
                                context.state.CurrentCreditReportProvider ==
                                null
                            ) {
                                if (res.data.CurrentReports[2]) {
                                    this.commit(
                                        "CurrentCreditReportProvider",
                                        2
                                    );
                                } else {
                                    this.commit(
                                        "CurrentCreditReportProvider",
                                        1
                                    );
                                }
                            }

                            this.commit(
                                "CreditReportHistory",
                                res.data.CreditReportHistory
                            );
                            this.commit(
                                "CreditReportHistoryDiffs",
                                res.data.Meta.Diffs
                            );
                            // this.commit("creditscoremeta", res.data.Meta);
                            if (res.data.CustomerDetails.length) {
                                this.commit(
                                    "VerificationStatus",
                                    res.data.CustomerDetails[0].status
                                );
                                // this.commit("MemberName", res.data.CustomerDetails[0]);
                                console.log(
                                    res.data.CustomerDetails[0].utmmedium,
                                    "utmmedium"
                                );
                                this.commit(
                                    "AgreementSigned",
                                    !(
                                        res.data.CustomerDetails[0]
                                            .utmmedium === "API" &&
                                        null ===
                                            res.data.CustomerDetails[0]
                                                .isverified
                                    )
                                );
                            }
                            null != res.data.CustomerDetails[0] &&
                                // null != res.data.CustomerDetails[0]["bankstatement_info"]
                                //     ? this.commit(
                                //           "BankStatementStatus",
                                //           res.data.CustomerDetails[0]["bankstatement_info"][
                                //               "bankstatement_status"
                                //           ]
                                //       )
                                //     : this.commit("BankStatementStatus", 0);
                                res.data.CustomerDetails[0];
                            resolve();
                        } else {
                            reject("GetCreditScores - Response Error");
                        }
                    })
                    .catch((err) => reject("GetCreditScores - Response Error"));
            });
        },
    },

    plugins: [vuexLocalStorage.plugin],
});

Sentry.configureScope((scope) => {
    scope.setExtra(store.state);
});

Vue.config.ignoredElements = ["ff-onboarding-widget"];

Vue.mixin({
    methods: {
        currency,
        IsRaffleEnabled() {
            return raffleEnabled;
        },
        CustomerSupportAddress() {
            return customerSupportAddress;
        },
        GetScoreDescription(score) {
            if (score == 0) {
                return "N/A";
            }
            if (score < 550) {
                return "Below Average";
            } else if (score > 549 && score < 625) {
                return "Fair";
            } else if (score > 624 && score < 700) {
                return "Good";
            } else if (score > 699 && score < 800) {
                return "Very Good";
            } else if (score > 799) {
                return "Excellent";
            }
        },
        GetCreditScoreDescriptionColor(score) {
            if (score == 0) {
                return "#E76044";
            }
            if (score < 550) {
                return "#EE7F03";
            } else if (score > 549 && score < 625) {
                return "#EE7F03";
            } else if (score > 624 && score < 700) {
                return "#00b809";
            } else if (score > 699 && score < 800) {
                return "#00b809";
            } else if (score > 799) {
                return "#00b809";
            }
        },
        BrandDomain(state) {
            switch (parseInt(brand)) {
                case 1:
                    return "https://www.tippla.com.au";
                case 2:
                    return "https://www.creditscoreplus.com.au";
                default:
                    return "https://www.tippla.com.au";
            }
        },
        BrandName(state) {
            return "Tippla";
        },
    },
});

const app = new Vue({
    el: "#app",
    components: {},
    router,
    store,
    watch: {
        $route: {
            deep: true,
            handler(to, from) {
                // this.$store.dispatch("GetLoginState").then(() => {
                //     if (!this.$store.state.LoggedIn && to.name === 'home') {
                //         location.href = '/login';
                //     }
                // });
                this.$store.commit("MobileNavOpen", false);
                if (to.path && this.$store.state.LoggedIn) {
                    trackAction("navigate.internal", to.path);
                }
            },
        },
    },
    created() {
        if (this.$route && this.$route.path && this.$store.state.LoggedIn) {
            trackAction("navigate.internal", this.$route.path);
        }
    },
});

axios.interceptors.response.use(
    function (response) {
        return response;
    },
    function (error) {
        if (
            typeof error === "object" &&
            typeof error.response === "object" &&
            error.response.hasOwnProperty("status") &&
            419 === error.response.status &&
            // AND current url does not include signup
            !window.location.href.includes("signup")
        ) {
            location.reload();
        }
        if (
            typeof error === "object" &&
            typeof error.response === "object" &&
            error.response.hasOwnProperty("status") &&
            401 === error.response.status
        ) {
            if (app.$store.state.LoggedIn) {
                app.$store.dispatch("Logout");
            }
        }
        return Promise.reject(error);
    }
);
